import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Alert, Spinner, FloatingLabel } from "react-bootstrap";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, MapPin, Clock } from "lucide-react";

const infoStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16 } },
};

const infoItemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

function ContactUs() {
  const shouldReduceMotion = useReducedMotion();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    setStatusMessage("");

    try {
      const response = await fetch("https://www.og-services.co.za/contact.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, message }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus("success");
        setStatusMessage("Thanks! Your message has been sent.");
        setName("");
        setEmail("");
        setMessage("");
      } else {
        setStatus("error");
        setStatusMessage(
          data.message || "Something went wrong. Please try again later."
        );
      }
    } catch (error) {
      console.error(error);
      setStatus("error");
      setStatusMessage("Network error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={shouldReduceMotion ? {} : { opacity: 0, y: -10 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
        <h1 className="ogs-sec-text">Contact Us</h1>
      </Container>

      <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
        <p className="ogs-pri-text">
          Have a project in mind, or just exploring options? Share a few details
          below and we'll get back to you with next steps.
        </p>

        {status && (
          <Alert variant={status === "success" ? "success" : "danger"} className="text-start">
            {statusMessage}
          </Alert>
        )}

        <Row className="text-start">
          <Col md={4} className="d-none d-md-block">
            <motion.div
              className="ogs-contact-info"
              variants={shouldReduceMotion ? {} : infoStagger}
              initial="hidden"
              animate="visible"
            >
              <motion.div className="ogs-contact-info-item" variants={shouldReduceMotion ? {} : infoItemVariants}>
                <Mail className="ogs-contact-info-icon" />
                <span>contact@og-services.co.za</span>
              </motion.div>
              <motion.div className="ogs-contact-info-item" variants={shouldReduceMotion ? {} : infoItemVariants}>
                <MapPin className="ogs-contact-info-icon" />
                <span>Based in South Africa</span>
              </motion.div>
              <motion.div className="ogs-contact-info-item" variants={shouldReduceMotion ? {} : infoItemVariants}>
                <Clock className="ogs-contact-info-icon" />
                <span>Help us help you — we reply within 24 hours</span>
              </motion.div>
            </motion.div>
          </Col>

          <Col md={8}>
            <Form onSubmit={handleSubmit}>
              <FloatingLabel controlId="formName" label="Name" className="mb-3">
                <Form.Control type="text" placeholder="Enter your name" value={name} onChange={(e) => setName(e.target.value)} required />
              </FloatingLabel>

              <FloatingLabel controlId="formEmail" label="Email address" className="mb-3">
                <Form.Control type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </FloatingLabel>

              <FloatingLabel controlId="formMessage" label="Message" className="mb-3">
                <Form.Control
                  as="textarea"
                  placeholder="Write your message here"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={4}
                  style={{ height: "120px" }}
                />
              </FloatingLabel>

              <Button variant="primary" type="submit" disabled={submitting} className="ogs-submit-btn">
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Sending...
                  </>
                ) : (
                  "Send"
                )}
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </motion.div>
  );
}

export default ContactUs;
